import { set as idbSet } from 'idb-keyval';

/* Web Share `AbortError` is what the browser throws when the user dismisses
 * the share sheet without choosing a destination. Treating it as a cancel
 * (not an error) lets the UI flash a neutral message instead of "failed". */
export class ExportCancelledError extends Error {
  constructor() {
    super('User cancelled share sheet');
    this.name = 'ExportCancelledError';
  }
}

/**
 * Export a canvas as JPEG. Best-effort across iOS Safari (web PWA), iOS
 * standalone-PWA (Add to Home Screen), and Capacitor (native iOS).
 *
 * Tries in order:
 *   1. Web Share API with files — works in all three modes; produces the
 *      native iOS share sheet (Save to Photos / Send to Mail / etc.).
 *   2. Anchor download — works in the regular browser only; iOS standalone
 *      PWA and Capacitor both ignore <a download>. We still attempt it as a
 *      graceful degradation for non-iOS users (Android, Chrome, Firefox).
 *
 * Throws `ExportCancelledError` when the user dismisses the share sheet,
 * so the caller can distinguish a cancel from a real failure.
 */
export async function exportCanvasJpeg(
  canvas: HTMLCanvasElement,
  filename = `intraop-${Date.now()}.jpg`,
  quality = 0.92,
): Promise<void> {
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas export failed'))),
      'image/jpeg',
      quality,
    );
  });

  // Web Share API (best UX on iOS, only path that actually works in
  // Capacitor's WKWebView).
  const file = new File([blob], filename, { type: 'image/jpeg' });
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: 'CADtomie Intraop' });
      return;
    } catch (err) {
      // AbortError = user dismissed the sheet. Surface it so the caller
      // can show a neutral message ("Export cancelled") instead of an
      // alarming "Export failed".
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new ExportCancelledError();
      }
      // Any other error — fall through to the anchor fallback for non-iOS
      // browsers, OR re-throw if we're clearly on iOS where anchor won't
      // work.
      if (typeof window !== 'undefined' && window.location.protocol === 'capacitor:') {
        // Native iOS — anchor download is a no-op. Re-throw the original.
        throw err;
      }
    }
  }

  // Fallback: anchor download / new-tab (Android / desktop browsers).
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  a.target = '_blank';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Save the blob to IndexedDB (local gallery). */
export async function saveCanvasLocal(
  canvas: HTMLCanvasElement,
  key = `intraop-${Date.now()}`,
  quality = 0.9,
): Promise<string> {
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Canvas export failed'))),
      'image/jpeg',
      quality,
    );
  });
  await idbSet(key, blob);
  return key;
}
